import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Edit3, Save, ExternalLink, Heart, Globe, MapPin, Facebook, Instagram, Youtube, Settings } from 'lucide-react';
import { toast } from 'sonner';

// TikTok and Pinterest icons (not in lucide-react)
const TikTokIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PinterestIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.4.04-3.44l1.43-6.09s-.36-.73-.36-1.82c0-1.7.99-2.98 2.22-2.98 1.05 0 1.55.78 1.55 1.72 0 1.05-.67 2.62-1.01 4.07-.29 1.21.61 2.2 1.8 2.2 2.16 0 3.82-2.28 3.82-5.57 0-2.91-2.09-4.95-5.08-4.95-3.46 0-5.49 2.6-5.49 5.28 0 1.05.4 2.17.91 2.78.1.12.11.23.08.35l-.34 1.38c-.05.22-.18.27-.42.16-1.56-.73-2.54-3-2.54-4.83 0-3.93 2.85-7.54 8.24-7.54 4.32 0 7.68 3.08 7.68 7.19 0 4.3-2.71 7.76-6.47 7.76-1.26 0-2.45-.66-2.86-1.43l-.78 2.96c-.28 1.08-1.04 2.44-1.55 3.27A12 12 0 1 0 12 0z"/>
  </svg>
);

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: me, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ display_name: '', bio: '', links: '', country: '', website: '' });
  const [avatarPreview, setAvatarPreview] = useState('');
  const fileRef = useRef(null);
  const isOwner = me?.id === userId;

  // Social link configurations
  const socialPlatforms = [
    { key: 'social_facebook', icon: Facebook, label: 'Facebook', color: 'hover:text-blue-600', url: 'facebook.com' },
    { key: 'social_instagram', icon: Instagram, label: 'Instagram', color: 'hover:text-pink-600', url: 'instagram.com' },
    { key: 'social_tiktok', icon: TikTokIcon, label: 'TikTok', color: 'hover:text-gray-900', url: 'tiktok.com' },
    { key: 'social_youtube', icon: Youtube, label: 'YouTube', color: 'hover:text-red-600', url: 'youtube.com' },
    { key: 'social_pinterest', icon: PinterestIcon, label: 'Pinterest', color: 'hover:text-red-700', url: 'pinterest.com' },
  ];

  // Check if profile has any social links
  const hasSocialLinks = profile && socialPlatforms.some(p => profile[p.key]);

  useEffect(() => {
    loadProfile();
    loadPosts();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const res = await api.get(`/users/${userId}`);
      setProfile(res.data);
      setForm({ 
        display_name: res.data.display_name || '', 
        bio: res.data.bio || '', 
        links: (res.data.links || []).join(', '),
        country: res.data.country || '',
        website: res.data.website || ''
      });
    } catch { toast.error('Profile not found'); }
  };

  const loadPosts = async () => {
    try {
      const res = await api.get('/posts');
      setPosts(res.data.filter(p => p.author_id === userId));
    } catch {}
  };

  const handleSave = async () => {
    try {
      const res = await api.put(`/users/${userId}`, {
        display_name: form.display_name,
        bio: form.bio,
        avatar: avatarPreview || undefined,
        links: form.links.split(',').map(l => l.trim()).filter(Boolean),
        country: form.country || undefined,
        website: form.website || undefined
      });
      setProfile(res.data);
      if (isOwner) updateUser(res.data);
      setEditing(false);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update profile'); }
  };

  const handleAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  };

  if (!profile) return <div className="text-center py-20 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto animate-in" data-testid="profile-page">
      {/* Header */}
      <div className="border border-border/40 p-6 md:p-8 mb-8">
        <div className="flex items-start gap-6">
          <div className="relative">
            <Avatar className="h-20 w-20 border-2 border-border">
              {(avatarPreview || profile.avatar) ? (
                <img src={`data:image/jpeg;base64,${avatarPreview || profile.avatar}`} alt="avatar" className="h-full w-full object-cover rounded-full" />
              ) : (
                <AvatarFallback className="bg-obsidian text-white text-xl font-serif">{(profile.display_name || 'U')[0].toUpperCase()}</AvatarFallback>
              )}
            </Avatar>
            {editing && (
              <>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} />
                <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 bg-gold text-obsidian rounded-full w-6 h-6 flex items-center justify-center text-xs">+</button>
              </>
            )}
          </div>
          <div className="flex-1">
            {editing ? (
              <div className="space-y-3">
                <Input data-testid="profile-edit-name" value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))} placeholder="Display Name" className="rounded-none border-0 border-b border-input bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 text-lg font-serif" />
                <Textarea data-testid="profile-edit-bio" value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} placeholder="Tell us about yourself..." className="rounded-none border-0 border-b border-input bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 resize-none text-sm" />
                <Input data-testid="profile-edit-country" value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} placeholder="Country (optional)" className="rounded-none border-0 border-b border-input bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 text-sm" />
                <Input data-testid="profile-edit-website" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="Website URL (optional)" className="rounded-none border-0 border-b border-input bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 text-sm" />
                <p className="text-xs text-muted-foreground pt-2">
                  <Link to="/settings" className="text-gold hover:underline flex items-center gap-1">
                    <Settings size={12} /> Edit social links in Settings
                  </Link>
                </p>
              </div>
            ) : (
              <>
                <h1 className="font-serif text-2xl">{profile.display_name}</h1>
                <p className="text-sm text-muted-foreground mb-1">@{profile.username}</p>
                
                {/* Country & Website */}
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {profile.country && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin size={12} /> {profile.country}
                    </span>
                  )}
                  {profile.website && (
                    <a 
                      href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs text-gold hover:underline flex items-center gap-1"
                    >
                      <Globe size={12} /> Website
                    </a>
                  )}
                </div>
                
                {profile.bio && <p className="text-sm font-sans mt-3 leading-relaxed">{profile.bio}</p>}
                
                {/* Social Connections */}
                {hasSocialLinks && (
                  <div className="mt-4 pt-4 border-t border-border/40">
                    <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Follow me</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {socialPlatforms.map(platform => {
                        const url = profile[platform.key];
                        if (!url) return null;
                        const Icon = platform.icon;
                        return (
                          <a
                            key={platform.key}
                            href={url.startsWith('http') ? url : `https://${url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`p-2 rounded-full bg-mist text-muted-foreground ${platform.color} transition-colors duration-200`}
                            title={platform.label}
                            data-testid={`social-link-${platform.key}`}
                          >
                            <Icon size={18} />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Legacy Links (for backwards compatibility) */}
                {profile.links?.length > 0 && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {profile.links.map((link, i) => (
                      <a key={i} href={link.startsWith('http') ? link : `https://${link}`} target="_blank" rel="noopener noreferrer" className="text-xs text-gold hover:underline flex items-center gap-1">
                        <ExternalLink size={10} /> {link}
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6">
          <Badge variant="outline" className="rounded-sm text-xs">{posts.length} posts</Badge>
          <Badge variant="outline" className="rounded-sm text-xs">{profile.role}</Badge>
          {isOwner && (
            editing ? (
              <Button data-testid="profile-save-btn" size="sm" onClick={handleSave} className="ml-auto rounded-sm text-xs uppercase tracking-wider bg-gold text-obsidian hover:bg-obsidian hover:text-white">
                <Save size={12} className="mr-1" /> Save
              </Button>
            ) : (
              <Button data-testid="profile-edit-btn" variant="outline" size="sm" onClick={() => setEditing(true)} className="ml-auto rounded-sm text-xs uppercase tracking-wider">
                <Edit3 size={12} className="mr-1" /> Edit
              </Button>
            )
          )}
        </div>
      </div>

      {/* Posts */}
      <h2 className="font-serif text-lg mb-4">Posts</h2>
      <div className="space-y-6">
        {posts.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No posts yet.</p>}
        {posts.map(post => (
          <div key={post.id} className="border border-border/40 p-4 hover:border-gold/30 transition-colors duration-500" data-testid={`profile-post-${post.id}`}>
            <p className="text-sm font-sans leading-relaxed">{post.content}</p>
            {post.media && post.media_type === 'image' && <img src={`data:image/jpeg;base64,${post.media}`} alt="post" className="mt-3 w-full max-h-60 object-cover img-art" />}
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Heart size={12} /> {post.likes?.length || 0}</span>
              <span>{new Date(post.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
